import React from "react";
import { Button, ProgressBar } from "react-bootstrap";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setCurrentDocument } from "../../store/thunk/user";
import "./DocumentList.css"; // Import custom CSS

// Function to check if all required document URLs are available
export const areDocumentsAvailable = (documents) => {
  const requiredFields = [
    "bankDetails",
    "headAndShoulderPhoto",
    "proofOfNationalInsurance",
    "passportOrBirthCertificate"
  ];
  // Check if the documents object is valid
  if (!documents || typeof documents !== 'object') {
    return false; // If documents object is undefined, null, or not an object, return false
  }

  // Validate required fields
  for (const field of requiredFields) {
    // Check if the field exists
    if (!documents.hasOwnProperty(field)) {
      return false; // Return false if the required field is missing
    }

    // If the field exists, check if it has a valid URL
    if (documents[field] != null && !documents[field].url) {
      return false; // If the field exists but the URL is missing or empty, return false
    }
  }

  return true; // If all required fields are present and valid, return true
};

const DocumentList = ({ documentList, setCurrentDocument }) => {
  const navigate = useNavigate();
  const goTo = (link) => {
    navigate(link);
  };

  // Convert the formData object into an array of items with a 'name' property
  const extractNamesAsArray = (data) => {
    return Object.entries(data)
      .filter(([_, value]) => value && value.name) // Filter entries with a name property
      .map(([key, value]) => {
        // Check if it's "complianceAndOtherDocuments" and validate its documents
        if (key === "complianceAndOtherDocuments" && !areDocumentsAvailable(value?.uploadedDocuments)) {
          // If the required documents are not available, set progress to 0
          return { ...value, key, progress: 0, step: 1 };
        }
        return { ...value, key }; // Otherwise, return as is
      });
  };

  const documents = extractNamesAsArray(documentList);
  return (
    <div className="list-group">
      {documents?.map((doc) => (
        <div
          key={doc?.key}
          className="list-group-item d-flex align-items-center"
        >
          {/* Left Side: Icon with New label */}
          <div className="position-relative me-3">
            <DescriptionIcon size={40} />
            <span
              className={
                doc?.progress === 100
                  ? "badge bg-success new-label"
                  : doc?.progress === 66
                  ? "badge bg-warning new-label"
                  : "badge bg-danger new-label"
              }
            >
              {doc?.progress === 100
                ? "Completed"
                : doc?.progress === 66
                ? "In Progress"
                : "New Form"}
            </span>
          </div>

          {/* Middle: Progress bar and completion percentage */}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between">
              <span>{doc?.name}</span>
              <span>{doc?.progress || 0}%</span>
            </div>
            <ProgressBar
              variant={
                doc?.progress === 100
                  ? "success"
                  : doc?.progress === 66
                  ? "warning"
                  : "danger"
              }
              now={doc?.progress || 0}
            />
          </div>

          {/* Right Side: Start Button */}
          <Button
            variant={
              doc?.progress === 100
                ? "success"
                : doc?.progress === 66
                ? "warning"
                : "danger"
            }
            className="ms-3"
            onClick={() => {
              setCurrentDocument(doc);
              goTo("/documents");
            }}
          >
            {doc?.progress === 100
              ? "View"
              : doc?.progress === 66
              ? "Resume"
              : "Start"}
          </Button>
        </div>
      ))}
    </div>
  );
};

const mapStateToProps = (state) => ({
  documentList: state.user.documentList,
});

export default connect(mapStateToProps, { setCurrentDocument })(DocumentList);
